import { useState, useCallback, useEffect, useRef, useMemo, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useGoldPrice, calcPositionLive, calcAccountLive } from '@/hooks/useGoldPrice';
import BottomSheet from '@/pages/profile/components/BottomSheet';
import { useToast } from '@/components/base/Toast';
import { useTradingStatus } from '@/hooks/useTradingStatus';

// 结算通知邮件（异步发送，不阻塞平仓流程）
async function notifySettleEmail(payload: {
  orderId: string;
  name: string;
  weight: number;
  direction: string;
  openPrice: number;
  closePrice: number;
  profit: number;
  closeTime: string;
  reason: string;
}) {
  try {
    await supabase.functions.invoke('send-settle-email', { body: payload });
  } catch (err) {
    console.error('[settle-email] exception:', err);
  }
}

// ========== 仓管费工具函数 ==========
function getStorageRate(name: string): number {
  const n = name.toLowerCase();
  if (n.includes('公斤') || n.includes('kg') || n.includes('kilo')) return 0.015; // 15元/1000克
  if (n.includes('银') || n.includes('ag') || n.includes('白银')) return 0.0016667; // 25元/15000克
  return 0.025; // 黄金默认 25元/1000克
}

function getDaysHeld(createdAt: string): number {
  const created = new Date(createdAt);
  const now = new Date();
  // 按自然日计算：创建日期到当前日期的天数差，最少1天
  const createdDate = new Date(created.getFullYear(), created.getMonth(), created.getDate());
  const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffMs = nowDate.getTime() - createdDate.getTime();
  return Math.max(1, Math.floor(diffMs / (24 * 60 * 60 * 1000)) + 1);
}

function calcOrderStorageFee(name: string, weight: number, createdAt: string): number {
  const rate = getStorageRate(name);
  const days = getDaysHeld(createdAt);
  // weight 已经是克数，直接计算
  return Number((weight * rate * days).toFixed(2));
}
// ===================================

interface Position {
  id: string;
  name: string;
  weight: number;
  direction: string;
  openPrice: number;
  currentPrice: number;
  settlePrice?: number;
  profit: number;
  originalMargin: number;
  currentMargin: number;
  marginRatio: number;
  liquidationLine: number;
  totalAmount: number;
  avgPrice: number;
  deferredFee: number;
  paidIn: number;
  paidOut: number;
  createTime: string;
  createdAt: string;
  storageFeeCharged: number;
  lockExpireAt: string;
}

interface ClosedPosition {
  id: string;
  name: string;
  weight: number;
  direction: string;
  openPrice: number;
  closePrice: number;
  profit: number;
  closeTime: string;
}

interface AccountSummary {
  availableMargin: number;
  usedMargin: number;
  longWeight: number;
  shortWeight: number;
  netWeight: number;
}

const tabs = [
  { key: 'hold', label: '订单' },
  { key: 'close', label: '已完成' },
];

function getRiskInfo(marginRate: number, liquidationLine: number) {
  const liqPct = liquidationLine * 100;
  const warn1 = liqPct + 10; // 30%
  const warn2 = liqPct + 5;  // 25%
  if (marginRate <= liqPct) return { label: '强平线', color: 'text-red-600', bg: 'bg-red-500', trackBg: 'bg-red-100', level: 'liquidation' };
  if (marginRate <= warn2) return { label: '高风险', color: 'text-red-500', bg: 'bg-red-400', trackBg: 'bg-red-100', level: 'high' };
  if (marginRate <= warn1) return { label: '中风险', color: 'text-amber-500', bg: 'bg-amber-400', trackBg: 'bg-amber-100', level: 'medium' };
  return { label: '正常', color: 'text-green-600', bg: 'bg-green-500', trackBg: 'bg-green-100', level: 'normal' };
}

function MarginRateBar({ marginRate, liquidationLine }: { marginRate: number; liquidationLine: number }) {
  const capped = Math.max(0, Math.min(100, marginRate));
  const fillPct = capped;
  const liqPct = Math.max(2, Math.min(98, liquidationLine * 100));
  const risk = getRiskInfo(marginRate, liquidationLine);

  return (
    <div className="mt-2">
      <div className="flex items-center justify-between mb-1">
        <span className="text-gray-400 text-xs">保证金率</span>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-bold ${risk.color}`}>{marginRate.toFixed(1)}%</span>
          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
            risk.level === 'normal' ? 'bg-green-50 text-green-600' :
            risk.level === 'medium' ? 'bg-amber-50 text-amber-600' :
            'bg-red-50 text-red-500'
          }`}>{risk.label}</span>
        </div>
      </div>
      <div className={`h-2 rounded-full ${risk.trackBg} overflow-hidden relative`}>
        <div
          className={`h-full rounded-full transition-all duration-500 ${risk.bg}`}
          style={{ width: `${fillPct}%` }}
        />
        {/* 强平线竖线标记 */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-red-600 z-10"
          style={{ left: `${liqPct}%` }}
        />
      </div>
      {/* 底部刻度标签 - 按实际百分比定位 */}
      <div className="relative h-4 mt-0.5">
        <span className="absolute left-0 top-0 text-gray-300 text-[9px]">0%</span>
        <span
          className="absolute top-0 text-red-500 text-[9px] font-medium"
          style={{ left: `${liqPct}%`, transform: 'translateX(-50%)' }}
        >
          强平 {(liquidationLine * 100).toFixed(0)}%
        </span>
        <span className="absolute right-0 top-0 text-gray-300 text-[9px]">100%</span>
      </div>
    </div>
  );
}

function PositionCard({
  pos,
  onPerform,
  onAddMargin,
  onClose,
}: {
  pos: Position & { liveProfit: number; marginRate: number; daysHeld: number; storageFee: number };
  onPerform: () => void;
  onAddMargin: () => void;
  onClose: () => void;
}) {
  const [pressed, setPressed] = useState(false);
  const profitColor = pos.liveProfit >= 0 ? 'text-red-500' : 'text-emerald-500';
  const profitSign = pos.liveProfit >= 0 ? '+' : '';
  const risk = getRiskInfo(pos.marginRate, pos.liquidationLine);

  // 涨跌闪烁动画状态
  const [flash, setFlash] = useState<'up' | 'down' | null>(null);
  const prevPriceRef = useRef(pos.currentPrice);

  // 按钮防抖 ref：防止连点触发多次
  const addMarginTapRef = useRef(false);
  const performTapRef = useRef(false);
  const closeTapRef = useRef(false);

  const handleAddMargin = (e: MouseEvent) => {
    e.stopPropagation();
    if (addMarginTapRef.current) return;
    addMarginTapRef.current = true;
    onAddMargin();
    setTimeout(() => { addMarginTapRef.current = false; }, 400);
  };
  const handlePerform = (e: MouseEvent) => {
    e.stopPropagation();
    if (performTapRef.current) return;
    performTapRef.current = true;
    onPerform();
    setTimeout(() => { performTapRef.current = false; }, 400);
  };
  const handleClose = (e: MouseEvent) => {
    e.stopPropagation();
    if (closeTapRef.current) return;
    closeTapRef.current = true;
    onClose();
    setTimeout(() => { closeTapRef.current = false; }, 400);
  };

  // 到期倒计时
  const expireInfo = useMemo(() => {
    if (!pos.lockExpireAt) return null;
    const expire = new Date(pos.lockExpireAt);
    const now = new Date();
    const diffMs = expire.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
    if (diffDays <= 0) return { label: '已到期', urgent: true, expired: true };
    if (diffDays === 1) return { label: '明天到期', urgent: true, expired: false };
    if (diffDays <= 3) return { label: `${diffDays}天后到期`, urgent: true, expired: false };
    return { label: `${diffDays}天后到期`, urgent: false, expired: false };
  }, [pos.lockExpireAt]);

  useEffect(() => {
    const prev = prevPriceRef.current;
    if (pos.currentPrice > prev) {
      setFlash('up');
    } else if (pos.currentPrice < prev) {
      setFlash('down');
    }
    prevPriceRef.current = pos.currentPrice;

    const timer = setTimeout(() => setFlash(null), 600);
    return () => clearTimeout(timer);
  }, [pos.currentPrice]);

  const flashBgClass = flash === 'up'
    ? 'animate-flash-up'
    : flash === 'down'
      ? 'animate-flash-down'
      : '';


  return (
    <div
      className={`bg-white rounded-xl shadow-sm overflow-hidden mb-3 transition-all duration-150 ${pressed ? 'scale-[0.98]' : ''}`}
      onPointerDown={() => setPressed(true)}
      onPointerUp={() => setPressed(false)}
      onPointerLeave={() => setPressed(false)}
    >
      {/* 到期提醒横幅 */}
      {expireInfo && expireInfo.urgent && (
        <div className={`px-4 py-1.5 flex items-center gap-2 ${
          expireInfo.expired ? 'bg-red-600' : 'bg-amber-500'
        }`}>
          <i className="ri-time-line text-white text-xs"></i>
          <span className="text-white text-xs font-medium">
            {expireInfo.expired ? '锁价已到期，系统将自动解除预约' : `⚠ 锁价${expireInfo.label}，请及时处理`}
          </span>
        </div>
      )}

      {/* 风险预警横幅 */}
      {risk.level !== 'normal' && (
        <div className={`px-4 py-1.5 flex items-center gap-2 ${
          risk.level === 'liquidation' ? 'bg-red-600' :
          risk.level === 'high' ? 'bg-red-500' : 'bg-amber-500'
        }`}>
          <i className="ri-alarm-warning-line text-white text-xs"></i>
          <span className="text-white text-xs font-medium">
            {risk.level === 'liquidation' ? '已触及强平线，订单将被系统解除' :
             risk.level === 'high' ? '保证金率过低，建议立即追加保证金' :
             '保证金率偏低，建议追加保证金'}
          </span>
        </div>
      )}

      {/* 卡片头 */}
      <div className="px-4 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center gap-2.5">
          <span className="bg-amber-100 text-amber-600 text-[10px] px-1.5 py-0.5 rounded font-medium">
            {pos.direction === 'IN' ? '买入 IN' : '卖出 OUT'}
          </span>
          <span className="text-brown-900 text-sm font-semibold">{pos.name} {pos.weight}g</span>
        </div>
        <span className="text-gray-400 text-xs">{pos.createTime}</span>
      </div>

      {/* 价格行 */}
      <div className="px-4 py-3 flex items-center justify-between">
        <div className="text-center flex-1">
          <p className="text-brown-900 text-lg font-bold">{pos.openPrice.toFixed(2)}</p>
          <p className="text-gray-400 text-xs mt-0.5">开仓价</p>
        </div>
        <div className="text-center flex-1">
          <p className={`text-brown-900 text-lg font-bold rounded px-1 py-0.5 transition-colors duration-300 ${flashBgClass}`}>
            {(pos.settlePrice ?? pos.currentPrice).toFixed(2)}
          </p>
          <p className="text-gray-400 text-xs mt-0.5">{pos.direction === 'OUT' ? '销售价格' : '回购价格'}</p>
        </div>
        <div className="text-center flex-1">
          <p className={`text-lg font-bold rounded px-1 py-0.5 transition-colors duration-300 ${profitColor} ${flashBgClass}`}>
            {profitSign}{pos.liveProfit.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
          </p>
          <p className="text-gray-400 text-xs mt-0.5">浮动盈亏</p>
        </div>
      </div>

      {/* 详情 */}
      <div className="px-4 pb-3">
        <div className="bg-brown-50/60 rounded-lg p-3">
          <div className="grid grid-cols-2 gap-x-4 gap-y-2 mb-2">
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">平均价格</span>
              <span className="text-brown-700 text-xs font-medium">¥{pos.avgPrice.toFixed(2)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">预约总额</span>
              <span className="text-brown-700 text-xs font-medium">¥{pos.totalAmount.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">保证金</span>
              <span className="text-brown-700 text-xs font-medium">¥{pos.currentMargin.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">仓管费</span>
              <div className="text-right">
                <span className="text-amber-600 text-xs font-medium">
                  ¥{pos.storageFee.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </span>
                <span className="block text-gray-400 text-[10px] mt-0.5">
                  已持仓 {pos.daysHeld} 天
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">保证金比例</span>
              <span className="text-brown-700 text-xs font-medium">{(pos.marginRatio * 100).toFixed(0)}%</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400 text-xs">强平线</span>
              <span className="text-red-400 text-xs font-medium">{(pos.liquidationLine * 100).toFixed(0)}%</span>
            </div>
            {pos.lockExpireAt && (
              <div className="flex items-center justify-between col-span-2">
                <span className="text-gray-400 text-xs">锁价到期</span>
                <span className={`text-xs font-medium ${expireInfo?.urgent ? 'text-red-500' : 'text-brown-700'}`}>
                  {new Date(pos.lockExpireAt).toLocaleString('zh-CN', {
                    month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
                  }).replace(/\//g, '-')} {expireInfo ? `(${expireInfo.label})` : ''}
                </span>
              </div>
            )}
          </div>
          {/* 风险进度条 */}
          <MarginRateBar marginRate={pos.marginRate} liquidationLine={pos.liquidationLine} />
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="px-4 pb-3 grid grid-cols-3 gap-2">
        <button
          onClick={handlePerform}
          className="py-3 rounded-lg bg-gold-500 text-brown-950 text-sm font-medium cursor-pointer active:scale-95 transition-transform whitespace-nowrap"
        >
          申请履约
        </button>
        <button
          onClick={handleAddMargin}
          className={`py-3 rounded-lg text-sm font-medium cursor-pointer active:scale-95 transition-transform whitespace-nowrap ${
            risk.level !== 'normal'
              ? 'bg-red-500 text-white animate-pulse'
              : 'bg-amber-500 text-white'
          }`}
        >
          追加保证金
        </button>
        <button
          onClick={handleClose}
          className="py-3 rounded-lg bg-red-50 text-red-500 text-sm font-medium cursor-pointer active:scale-95 transition-transform border border-red-100 hover:bg-red-100 whitespace-nowrap"
        >
          解除预约
        </button>
      </div>
    </div>
  );
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { getPrice, getBidPrice, isLive, isPriceLive, simulateWave, stopWave } = useGoldPrice();
  const { status: tradingStatus } = useTradingStatus();
  const [activeTab, setActiveTab] = useState<'hold' | 'close'>('hold');
  const [accountSummary, setAccountSummary] = useState<AccountSummary>({ availableMargin: 0, usedMargin: 0, longWeight: 0, shortWeight: 0, netWeight: 0 });
  const [positions, setPositions] = useState<Position[]>([]);
  const [closedPositions, setClosedPositions] = useState<ClosedPosition[]>([]);
  const [typeFilter, setTypeFilter] = useState<'all' | 'IN' | 'OUT'>('all');
  const [typeOpen, setTypeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // 弹窗状态
  const [modalType, setModalType] = useState<'none' | 'addMargin' | 'close'>('none');
  const [modalPosId, setModalPosId] = useState<string | null>(null);
  const [inputAmount, setInputAmount] = useState('');
  const [modalError, setModalError] = useState('');
  const [closing, setClosing] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // 警告弹窗
  const [riskWarning, setRiskWarning] = useState<{ posId: string; level: string } | null>(null);
  // 已提醒过的风险记录：orderId -> 等级。同一订单同一等级只提醒一次，
  // 避免用户点「立即追加」后弹窗又自动弹回、把追加面板卡住。
  const riskAckRef = useRef<Map<string, string>>(new Map());

  // 已自动强平的订单ID（防止重复强平）
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const autoLiquidatedRef = useRef<Set<string>>(new Set());
  const liquidatingRef = useRef<Set<string>>(new Set());
  // 自动结算/强平检查节流：10秒内最多跑一次，避免金价每200ms跳动就触发一次网络请求
  // 拆分为两个独立 ref，避免到期结算先更新时间戳导致强平检查被跳过
  const lastSettleCheckRef = useRef(0);
  const lastLiquidateCheckRef = useRef(0);
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('[data-dropdown="type"]')) setTypeOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const openModal = (type: 'addMargin' | 'close', posId: string) => {
    setModalType(type);
    setModalPosId(posId);
    setInputAmount('');
    setModalError('');
    setSubmitting(false);
  };

  const closeModal = () => {
    setModalType('none');
    setModalPosId(null);
    setInputAmount('');
    setModalError('');
    setClosing(false);
    setSubmitting(false);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;
      if (!userId) { setLoading(false); return; }

      const [summaryRes, openRes, closedRes] = await Promise.all([
        supabase.from('account_summary').select('*').eq('user_id', userId).maybeSingle(),
        supabase.from('orders').select('*').eq('status', 'open').eq('user_id', userId).order('created_at', { ascending: false }),
        supabase.from('orders').select('*').eq('status', 'closed').eq('user_id', userId).order('created_at', { ascending: false }),
      ]);

      if (summaryRes.error) throw summaryRes.error;
      if (openRes.error) throw openRes.error;
      if (closedRes.error) throw closedRes.error;

      if (summaryRes.data) {
        setAccountSummary({
          availableMargin: Number(summaryRes.data.available_margin) || 0,
          usedMargin: Number(summaryRes.data.used_margin) || 0,
          longWeight: Number(summaryRes.data.long_weight) || 0,
          shortWeight: Number(summaryRes.data.short_weight) || 0,
          netWeight: Number(summaryRes.data.net_weight) || 0,
        });
      }

      setPositions((openRes.data || []).map((row) => ({
        id: row.id,
        name: row.name,
        weight: Number(row.weight) || 0,
        direction: row.direction,
        openPrice: Number(row.open_price) || 0,
        currentPrice: Number(row.current_price) || 0,
        profit: Number(row.profit) || 0,
        originalMargin: Number(row.original_margin) || 0,
        currentMargin: Number(row.current_margin) || 0,
        marginRatio: Number(row.margin_ratio) || 0.06,
        liquidationLine: Number(row.liquidation_line) || 0.20,
        totalAmount: Number(row.total_amount) || 0,
        avgPrice: (Number(row.weight) > 0 ? (Number(row.total_amount) || 0) / Number(row.weight) : 0),
        deferredFee: Number(row.deferred_fee) || 0,
        paidIn: Number(row.paid_in) || 0,
        paidOut: Number(row.paid_out) || 0,
        createTime: row.create_time || '',
        createdAt: row.created_at || '',
        storageFeeCharged: Number(row.storage_fee_charged) || 0,
        lockExpireAt: row.lock_expire_at || '',
      })));

      setClosedPositions((closedRes.data || []).map((row) => ({
        id: row.id,
        name: row.name,
        weight: Number(row.weight) || 0,
        direction: row.direction,
        openPrice: Number(row.open_price) || 0,
        closePrice: Number(row.close_price) || 0,
        profit: Number(row.profit) || 0,
        closeTime: row.close_time || '',
      })));
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // 为防止并发重复扣费，增加一个正在执行中的标志
  const storageFeeDeductingRef = useRef(false);

  // ===== 每日仓管费自动扣除 =====
  useEffect(() => {
    const deduct = async () => {
      // 防止并发重复触发
      if (storageFeeDeductingRef.current) return;
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || positions.length === 0) return;
      storageFeeDeductingRef.current = true;

      // 获取最新账户余额
      const { data: summary } = await supabase.from('account_summary')
        .select('available_margin').eq('user_id', user.id).maybeSingle();
      let available = Number(summary?.available_margin) || 0;

      let totalDeducted = 0;
      const updatedPositions: Position[] = [];

      for (const pos of positions) {
        if (!pos.createdAt) continue;

        const totalFee = calcOrderStorageFee(pos.name, pos.weight, pos.createdAt);
        const diff = totalFee - pos.storageFeeCharged;

        if (diff > 0.01) {
          // 扣费：更新订单已扣仓管费（余额不够也扣，允许负数）
          await supabase.from('orders').update({
            storage_fee_charged: totalFee,
          }).eq('id', pos.id);

          available -= diff;
          totalDeducted += diff;

          // 写入仓管费扣款流水
          const feeTime = new Date().toLocaleString('zh-CN', {
            month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
          }).replace(/\//g, '-');
          await supabase.from('payments').insert({
            id: `${pos.id}_SF_${Date.now()}`,
            user_id: user.id,
            order_id: pos.id,
            product: pos.name,
            amount: diff,
            status: 'paid',
            payment_method: '仓管费',
            create_time: feeTime,
          });

          updatedPositions.push({ ...pos, storageFeeCharged: totalFee });
        } else {
          updatedPositions.push(pos);
        }
      }

      if (totalDeducted > 0) {
        // 更新账户余额（允许扣成负数）
        await supabase.from('account_summary').update({
          available_margin: available,
        }).eq('user_id', user.id);

        // 更新本地状态
        setPositions(updatedPositions);
        setAccountSummary((prev) => ({
          ...prev,
          availableMargin: prev.availableMargin - totalDeducted,
        }));

        showToast(`已扣除仓管费 ¥${totalDeducted.toFixed(2)}`, 'info');
      }
      storageFeeDeductingRef.current = false;
    };

    deduct();
  }, [positions.length]); // 只在持仓列表变化时执行（页面加载后）
  // ================================

  // 关闭页面级独立模拟波动；金价统一由 useGoldPrice 全局轮询驱动
  useEffect(() => {
    // 不再启动 simulateWave，所有价格统一由全局行情引擎管理
    return () => {
      // 如有残留 wave 一并清理
      const labels = Array.from(new Set(positions.map((p) => p.name).filter(Boolean)));
      for (const label of labels) {
        stopWave(label);
      }
    };
  }, [positions, stopWave]);

  // 实时计算盈亏、保证金率、持仓天数、仓管费
  // 做空(OUT)：开仓按回购价(bid)，盈亏/结算按销售价(ask)
  // 做多(IN)：开仓按销售价(ask)，盈亏/结算按回购价(bid)
  const livePositions = positions.map((pos) => {
    const settlePrice = pos.direction === 'OUT'
      ? (getPrice(pos.name) || pos.currentPrice || pos.openPrice)
      : (getBidPrice(pos.name) || pos.currentPrice || pos.openPrice);
    const { liveProfit, marginRate } = calcPositionLive(pos.direction, pos.weight, pos.openPrice, settlePrice, pos.currentMargin, pos.originalMargin);
    const daysHeld = getDaysHeld(pos.createdAt);
    const storageFee = calcOrderStorageFee(pos.name, pos.weight, pos.createdAt);
    return { ...pos, currentPrice: settlePrice, settlePrice, liveProfit, marginRate, daysHeld, storageFee };
  });

  // 计算买入均价（加权平均）
  const buyPositions = livePositions.filter((p) => p.direction === 'IN');
  const totalBuyWeight = buyPositions.reduce((sum, p) => sum + p.weight, 0);
  const totalBuyAmount = buyPositions.reduce((sum, p) => sum + p.openPrice * p.weight, 0);
  const avgBuyPrice = totalBuyWeight > 0 ? totalBuyAmount / totalBuyWeight : 0;

  // 风险预警：保证金率触及30%/25%时弹窗提醒
  useEffect(() => {
    // 已有弹窗、或正在操作追加/解除面板时不打断
    if (riskWarning || modalType !== 'none') return;
    for (const pos of livePositions) {
      const liqPct = pos.liquidationLine * 100;
      if (pos.marginRate <= liqPct + 10 && pos.marginRate > liqPct) {
        const level = pos.marginRate <= liqPct + 5 ? 'high' : 'medium';
        // 同一订单同一等级已提醒过则跳过（风险等级升级时才会重新提醒）
        if (riskAckRef.current.get(pos.id) === level) continue;
        riskAckRef.current.set(pos.id, level);
        setRiskWarning({ posId: pos.id, level });
        break;
      }
    }
  }, [livePositions, riskWarning, modalType]);

  // 自动到期平仓：锁价到期后自动结算
  useEffect(() => {
    const autoSettle = async () => {
      // 节流：10秒内最多检查一次
      const now = Date.now();
      if (now - lastSettleCheckRef.current < 10000) return;
      lastSettleCheckRef.current = now;

      // 行情未连接时绝不用兜底/占位价结算，避免用错误价格误平仓
      if (!isLive) return;

      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData.session?.user;
      if (!user) return;

      for (const pos of livePositions) {
        if (!pos.lockExpireAt) continue;
        const expire = new Date(pos.lockExpireAt);
        if (expire.getTime() > Date.now()) continue;
        if (liquidatingRef.current.has(pos.id)) continue;

        // 强制校验：结算价必须有效，否则跳过不清仓
        const settlePrice = pos.settlePrice || pos.currentPrice;
        if (!settlePrice || settlePrice <= 0) {
          console.warn('[AutoSettle] 跳过订单', pos.id, '，行情数据无效:', settlePrice);
          continue;
        }
        // 该品种必须已拿到真实实时行情，否则报价可能来自兜底占位价，绝不结算
        if (!isPriceLive(pos.name)) {
          console.warn('[AutoSettle] 跳过订单', pos.id, '，该品种无实时行情:', pos.name);
          continue;
        }

        liquidatingRef.current.add(pos.id);

        try {
          // 确认订单仍是 open
          const { data: orderCheck } = await supabase
            .from('orders')
            .select('status')
            .eq('id', pos.id)
            .maybeSingle();
          if (!orderCheck || orderCheck.status !== 'open') {
            liquidatingRef.current.delete(pos.id);
            continue;
          }

          const releaseAmount = pos.currentMargin;
          const closeProfit = pos.liveProfit;
          const netReturn = releaseAmount + closeProfit;
          const closeTime = new Date().toLocaleString('zh-CN', {
            month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
          }).replace(/\//g, '-');

          // 1. 取消关联支付单
          await supabase.from('payments').update({ status: 'cancelled' }).eq('order_id', pos.id).eq('status', 'pending');

          // 2. 更新订单为 closed
          await supabase.from('orders').update({
            status: 'closed',
            close_price: pos.settlePrice || pos.currentPrice,
            close_time: closeTime,
            profit: closeProfit,
          }).eq('id', pos.id);

          // 3. 更新账户汇总
          const { data: summary } = await supabase.from('account_summary').select('*').eq('user_id', user.id).maybeSingle();
          const newLong = pos.direction === 'IN' ? Math.max(0, (Number(summary?.long_weight) || 0) - pos.weight) : (Number(summary?.long_weight) || 0);
          const newShort = pos.direction === 'OUT' ? Math.max(0, (Number(summary?.short_weight) || 0) - pos.weight) : (Number(summary?.short_weight) || 0);

          await supabase.from('account_summary').update({
            used_margin: Math.max(0, (Number(summary?.used_margin) || 0) - releaseAmount),
            available_margin: Math.max(0, (Number(summary?.available_margin) || 0) + netReturn),
            long_weight: newLong,
            short_weight: newShort,
            net_weight: newLong - newShort,
          }).eq('user_id', user.id);

          // 更新本地状态
          setClosedPositions((prev) => [{
            id: pos.id,
            name: pos.name,
            weight: pos.weight,
            direction: pos.direction,
            openPrice: pos.openPrice,
            closePrice: pos.settlePrice || pos.currentPrice,
            profit: closeProfit,
            closeTime,
          }, ...prev]);
          setPositions((prev) => prev.filter((p) => p.id !== pos.id));
          setAccountSummary((prev) => {
            const newLong2 = pos.direction === 'IN' ? Math.max(0, prev.longWeight - pos.weight) : prev.longWeight;
            const newShort2 = pos.direction === 'OUT' ? Math.max(0, prev.shortWeight - pos.weight) : prev.shortWeight;
            return {
              ...prev,
              usedMargin: Math.max(0, prev.usedMargin - releaseAmount),
              availableMargin: Math.max(0, prev.availableMargin + netReturn),
              longWeight: newLong2,
              shortWeight: newShort2,
              netWeight: newLong2 - newShort2,
            };
          });

          notifySettleEmail({
            orderId: pos.id,
            name: pos.name,
            weight: pos.weight,
            direction: pos.direction,
            openPrice: pos.openPrice,
            closePrice: pos.settlePrice || pos.currentPrice,
            profit: closeProfit,
            closeTime,
            reason: '到期自动结算',
          });

          showToastRef.current(`【到期自动结算】${pos.name} ${pos.weight}g 锁价已到期，订单已自动解除`, 'info');
        } catch (err) {
          console.error('[AutoSettle] 到期结算失败:', pos.id, err);
        } finally {
          liquidatingRef.current.delete(pos.id);
        }
      }
    };

    autoSettle();
  }, [livePositions, isLive, isPriceLive]);

  // 自动强制平仓：保证金率 <= 强平线时自动平仓
  useEffect(() => {
    const liquidate = async () => {
      // 节流：10秒内最多检查一次
      const now = Date.now();
      if (now - lastLiquidateCheckRef.current < 10000) return;
      lastLiquidateCheckRef.current = now;

      // 行情未连接时绝不强平，避免用兜底/占位价误平仓
      if (!isLive) return;

      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData.session?.user;
      if (!user) return;

      for (const pos of livePositions) {
        const liqPct = pos.liquidationLine * 100;
        if (pos.marginRate > liqPct) continue;
        if (liquidatingRef.current.has(pos.id)) continue;

        // 强制校验：结算价必须有效，否则跳过不清仓
        const settlePrice = pos.settlePrice || pos.currentPrice;
        if (!settlePrice || settlePrice <= 0) {
          console.warn('[AutoLiquidate] 跳过订单', pos.id, '，行情数据无效:', settlePrice);
          continue;
        }
        // 该品种必须已拿到真实实时行情，否则报价可能来自兜底占位价，绝不强平
        if (!isPriceLive(pos.name)) {
          console.warn('[AutoLiquidate] 跳过订单', pos.id, '，该品种无实时行情:', pos.name);
          continue;
        }

        liquidatingRef.current.add(pos.id);

        try {
          // 先查数据库确认订单还是 open 状态（以数据库为准，不用前端标记）
          const { data: orderCheck } = await supabase
            .from('orders')
            .select('status')
            .eq('id', pos.id)
            .maybeSingle();
          if (!orderCheck || orderCheck.status !== 'open') {
            liquidatingRef.current.delete(pos.id);
            continue;
          }

          const releaseAmount = pos.currentMargin;
          const closeProfit = pos.liveProfit;
          const netReturn = releaseAmount + closeProfit;
          const closeTime = new Date().toLocaleString('zh-CN', {
            month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
          }).replace(/\//g, '-');

          // 1. 取消关联的履约支付单
          await supabase.from('payments').update({ status: 'cancelled' }).eq('order_id', pos.id).eq('status', 'pending');

          // 2. 更新订单为 closed
          await supabase.from('orders').update({
            status: 'closed',
            close_price: pos.settlePrice || pos.currentPrice,
            close_time: closeTime,
            profit: closeProfit,
          }).eq('id', pos.id);

          // 3. 更新账户汇总（结算盈亏）
          const { data: summary } = await supabase.from('account_summary').select('*').eq('user_id', user.id).maybeSingle();
          const newLong = pos.direction === 'IN' ? Math.max(0, (Number(summary?.long_weight) || 0) - pos.weight) : (Number(summary?.long_weight) || 0);
          const newShort = pos.direction === 'OUT' ? Math.max(0, (Number(summary?.short_weight) || 0) - pos.weight) : (Number(summary?.short_weight) || 0);

          await supabase.from('account_summary').update({
            used_margin: Math.max(0, (Number(summary?.used_margin) || 0) - releaseAmount),
            available_margin: Math.max(0, (Number(summary?.available_margin) || 0) + netReturn),
            long_weight: newLong,
            short_weight: newShort,
            net_weight: newLong - newShort,
          }).eq('user_id', user.id);

          // 更新本地状态
          setClosedPositions((prev) => [{
            id: pos.id,
            name: pos.name,
            weight: pos.weight,
            direction: pos.direction,
            openPrice: pos.openPrice,
            closePrice: pos.settlePrice || pos.currentPrice,
            profit: closeProfit,
            closeTime,
          }, ...prev]);
          setPositions((prev) => prev.filter((p) => p.id !== pos.id));
          setAccountSummary((prev) => {
            const newLong2 = pos.direction === 'IN' ? Math.max(0, prev.longWeight - pos.weight) : prev.longWeight;
            const newShort2 = pos.direction === 'OUT' ? Math.max(0, prev.shortWeight - pos.weight) : prev.shortWeight;
            return {
              ...prev,
              usedMargin: Math.max(0, prev.usedMargin - releaseAmount),
              availableMargin: Math.max(0, prev.availableMargin + netReturn),
              longWeight: newLong2,
              shortWeight: newShort2,
              netWeight: newLong2 - newShort2,
            };
          });

          notifySettleEmail({
            orderId: pos.id,
            name: pos.name,
            weight: pos.weight,
            direction: pos.direction,
            openPrice: pos.openPrice,
            closePrice: pos.settlePrice || pos.currentPrice,
            profit: closeProfit,
            closeTime,
            reason: '系统强制平仓',
          });

          showToastRef.current(`【系统强平】${pos.name} ${pos.weight}g 已触及强平线，订单已被强制解除`, 'error');
        } catch (err) {
          console.error('[AutoLiquidate] 强平失败，保留订单:', pos.id, err);
        } finally {
          liquidatingRef.current.delete(pos.id);
        }
      }
    };

    liquidate();
  }, [livePositions, isLive, isPriceLive]);

  const { liveAvailable } = calcAccountLive(accountSummary.availableMargin, accountSummary.usedMargin);
  const totalFloatingProfit = livePositions.reduce((sum, p) => sum + p.liveProfit, 0);
  const totalAssets = liveAvailable + accountSummary.usedMargin + totalFloatingProfit;

  // 涨跌闪烁动画（汇总区）
  const [flashTotal, setFlashTotal] = useState<'up' | 'down' | null>(null);
  const prevTotalAssetsRef = useRef(totalAssets);
  const [flashProfit, setFlashProfit] = useState<'up' | 'down' | null>(null);
  const prevProfitRef = useRef(totalFloatingProfit);
  const [flashAvail, setFlashAvail] = useState<'up' | 'down' | null>(null);
  const prevAvailRef = useRef(liveAvailable);

  useEffect(() => {
    const prev = prevTotalAssetsRef.current;
    if (totalAssets > prev) setFlashTotal('up');
    else if (totalAssets < prev) setFlashTotal('down');
    prevTotalAssetsRef.current = totalAssets;
    const t = setTimeout(() => setFlashTotal(null), 600);
    return () => clearTimeout(t);
  }, [totalAssets]);

  useEffect(() => {
    const prev = prevProfitRef.current;
    if (totalFloatingProfit > prev) setFlashProfit('up');
    else if (totalFloatingProfit < prev) setFlashProfit('down');
    prevProfitRef.current = totalFloatingProfit;
    const t = setTimeout(() => setFlashProfit(null), 600);
    return () => clearTimeout(t);
  }, [totalFloatingProfit]);

  useEffect(() => {
    const prev = prevAvailRef.current;
    if (liveAvailable > prev) setFlashAvail('up');
    else if (liveAvailable < prev) setFlashAvail('down');
    prevAvailRef.current = liveAvailable;
    const t = setTimeout(() => setFlashAvail(null), 600);
    return () => clearTimeout(t);
  }, [liveAvailable]);

  const flashClass = (dir: 'up' | 'down' | null) =>
    dir === 'up' ? 'animate-flash-up' : dir === 'down' ? 'animate-flash-down' : '';

  const filteredPositions = livePositions.filter((pos) => {
    if (typeFilter !== 'all' && pos.direction !== typeFilter) return false;
    return true;
  });

  const activeModalPos = modalPosId ? livePositions.find((p) => p.id === modalPosId) || null : null;
  const baseModalPos = modalPosId ? positions.find((p) => p.id === modalPosId) || null : null;

  // 追加保证金防重复点击锁（同步 ref，比 state 更快）
  const addMarginLockRef = useRef(false);

  // ===== 追加保证金 =====
  const handleAddMarginConfirm = useCallback(async () => {
    if (!activeModalPos || !baseModalPos) return;
    if (addMarginLockRef.current) return; // 同步锁，防连点
    addMarginLockRef.current = true;

    const amount = parseFloat(inputAmount);
    if (!amount || amount <= 0) { setModalError('请输入有效金额'); addMarginLockRef.current = false; return; }
    if (amount > liveAvailable) {
      setModalError(`可用资金不足（当前可用 ¥${liveAvailable.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}）`);
      addMarginLockRef.current = false;
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) { addMarginLockRef.current = false; return; }

    setSubmitting(true);
    try {
      // 幂等保护：检查该订单最近 5 秒内是否已有追加保证金的支付记录
      const fiveSecAgo = new Date(Date.now() - 5000).toISOString();
      const { data: recentPayments } = await supabase
        .from('payments')
        .select('id, create_time')
        .eq('user_id', user.id)
        .eq('order_id', baseModalPos.id)
        .eq('payment_method', '余额支付')
        .gt('created_at', fiveSecAgo)
        .limit(1);
      if (recentPayments && recentPayments.length > 0) {
        showToast('追加保证金已提交，请稍后刷新查看结果', 'error');
        return;
      }

      const { data: summary } = await supabase.from('account_summary').select('available_margin, used_margin').eq('user_id', user.id).maybeSingle();
      const currentAvailable = Number(summary?.available_margin) || 0;

      // 扣可用资金，加到订单保证金
      await supabase.from('account_summary').update({
        available_margin: Math.max(0, currentAvailable - amount),
        used_margin: (Number(summary?.used_margin) || 0) + amount,
      }).eq('user_id', user.id);

      await supabase.from('orders').update({
        current_margin: baseModalPos.currentMargin + amount,
        paid_in: baseModalPos.paidIn + amount,
      }).eq('id', baseModalPos.id);

      // 写入追加保证金的支付记录，让扣款进入资金流水
      const addTime = new Date().toLocaleString('zh-CN', {
        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
      }).replace(/\//g, '-');
      await supabase.from('payments').insert({
        id: `${baseModalPos.id}_AM_${Date.now()}`,
        user_id: user.id,
        order_id: baseModalPos.id,
        product: baseModalPos.name,
        amount,
        status: 'paid',
        payment_method: '余额支付',
        create_time: addTime,
        created_at: new Date().toISOString(),
      });

      setPositions((prev) => prev.map((p) => p.id === baseModalPos.id ? {
        ...p,
        currentMargin: p.currentMargin + amount,
        paidIn: p.paidIn + amount,
      } : p));
      setAccountSummary((prev) => ({
        ...prev,
        availableMargin: Math.max(0, prev.availableMargin - amount),
        usedMargin: prev.usedMargin + amount,
      }));

      showToast('追加保证金成功', 'success');
      closeModal();
    } catch (err) {
      console.error('[AddMargin] 追加保证金失败:', err);
      showToast('追加保证金失败，请重试', 'error');
    } finally {
      setSubmitting(false);
      addMarginLockRef.current = false;
    }
  }, [activeModalPos, baseModalPos, inputAmount, liveAvailable, showToast]);

  // ===== 解除预约（平仓）=====
  const handleCloseConfirm = useCallback(async () => {
    if (!activeModalPos || !baseModalPos || closing) return;

    // 强制校验接口数据：结算价必须有效
    const settlePrice = activeModalPos.settlePrice || activeModalPos.currentPrice;
    if (!settlePrice || settlePrice <= 0) {
      showToast('行情数据异常，请稍后重试平仓', 'error');
      setClosing(false);
      return;
    }
    // 该品种必须有真实实时行情，避免用兜底占位价结算
    if (!isLive || !isPriceLive(activeModalPos.name)) {
      showToast('实时行情未连接，请稍后重试', 'error');
      setClosing(false);
      return;
    }

    if (tradingStatus && tradingStatus.enabled && !tradingStatus.isOpen) {
      showToast(tradingStatus.manualPaused ? '暂停交易中，无法结算' : '当前休市，无法结算', 'error');
      return;
    }

    setClosing(true);

    // 超时保护：15秒后自动解除 loading，防止网络 hang 住导致一直转圈
    const timeoutId = setTimeout(() => {
      setClosing(false);
      showToast('请求超时，请检查网络连接后重试', 'error');
    }, 15000);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const user = sessionData.session?.user;
      if (!user) {
        showToast('登录状态已失效，请重新登录', 'error');
        return;
      }

      const releaseAmount = baseModalPos.currentMargin;
      const closeProfit = activeModalPos.liveProfit;
      const netReturn = releaseAmount + closeProfit;
      const closeTime = new Date().toLocaleString('zh-CN', {
        month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
      }).replace(/\//g, '-');

      // 并行执行：取消支付单 + 更新订单 + 读取账户汇总，减少网络往返
      const [, , summaryRes] = await Promise.all([
        supabase.from('payments').update({ status: 'cancelled' }).eq('order_id', baseModalPos.id).eq('status', 'pending'),
        supabase.from('orders').update({
          status: 'closed',
          close_price: activeModalPos.settlePrice || activeModalPos.currentPrice,
          close_time: closeTime,
          profit: closeProfit,
        }).eq('id', baseModalPos.id),
        supabase.from('account_summary').select('*').eq('user_id', user.id).maybeSingle(),
      ]);

      const summary = summaryRes.data;

      const newLong = activeModalPos.direction === 'IN'
        ? Math.max(0, (Number(summary?.long_weight) || 0) - activeModalPos.weight)
        : (Number(summary?.long_weight) || 0);
      const newShort = activeModalPos.direction === 'OUT'
        ? Math.max(0, (Number(summary?.short_weight) || 0) - activeModalPos.weight)
        : (Number(summary?.short_weight) || 0);

      await supabase.from('account_summary').update({
        used_margin: Math.max(0, (Number(summary?.used_margin) || 0) - releaseAmount),
        available_margin: Math.max(0, (Number(summary?.available_margin) || 0) + netReturn),
        long_weight: newLong,
        short_weight: newShort,
        net_weight: newLong - newShort,
      }).eq('user_id', user.id);

      setClosedPositions((prev) => [{
        id: activeModalPos.id,
        name: activeModalPos.name,
        weight: activeModalPos.weight,
        direction: activeModalPos.direction,
        openPrice: activeModalPos.openPrice,
        closePrice: activeModalPos.settlePrice || activeModalPos.currentPrice,
        profit: closeProfit,
        closeTime,
      }, ...prev]);
      setPositions((prev) => prev.filter((p) => p.id !== activeModalPos.id));
      setAccountSummary((prev) => {
        const newLong2 = activeModalPos.direction === 'IN' ? Math.max(0, prev.longWeight - activeModalPos.weight) : prev.longWeight;
        const newShort2 = activeModalPos.direction === 'OUT' ? Math.max(0, prev.shortWeight - activeModalPos.weight) : prev.shortWeight;
        return {
          ...prev,
          usedMargin: Math.max(0, prev.usedMargin - releaseAmount),
          availableMargin: Math.max(0, prev.availableMargin + netReturn),
          longWeight: newLong2,
          shortWeight: newShort2,
          netWeight: newLong2 - newShort2,
        };
      });
      // 发送结算通知邮件（不阻塞平仓）
      notifySettleEmail({
        orderId: baseModalPos.id,
        name: baseModalPos.name,
        weight: baseModalPos.weight,
        direction: baseModalPos.direction,
        openPrice: activeModalPos.openPrice,
        closePrice: activeModalPos.settlePrice || activeModalPos.currentPrice,
        profit: closeProfit,
        closeTime,
        reason: '客户手动平仓',
      });

      showToast('预约已解除，保证金及盈亏已结算到账户', 'success');
      closeModal();
    } catch (err) {
      console.error('[CloseOrder] 解除预约失败:', err);
      showToast('解除预约失败，请重试', 'error');
    } finally {
      clearTimeout(timeoutId);
      setClosing(false);
    }
  }, [activeModalPos, baseModalPos, closing, showToast, tradingStatus, isLive, isPriceLive]);

  return (
    <div className="min-h-screen bg-brown-50 pb-28">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white px-5 pt-safe pb-3 shadow-sm">
        <div className="flex items-center gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as 'hold' | 'close')}
              className={`pb-2 text-base font-semibold cursor-pointer whitespace-nowrap transition-colors relative ${activeTab === tab.key ? 'text-brown-900' : 'text-gray-400'}`}
            >
              {tab.label}
              {activeTab === tab.key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-gold-500 rounded-full" />}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'hold' && (
        <>
          {/* 暂停交易提示 */}
          {tradingStatus && tradingStatus.enabled && !tradingStatus.isOpen && (
            <div className={`mx-3 mt-3 rounded-lg px-3 py-2.5 flex items-center gap-2 ${
              tradingStatus.manualPaused ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'
            }`}>
              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${tradingStatus.manualPaused ? 'bg-red-400' : 'bg-amber-400'}`} />
              <p className={`text-xs ${tradingStatus.manualPaused ? 'text-red-600' : 'text-amber-700'}`}>
                {tradingStatus.manualPaused
                  ? `暂停交易中，当前无法买入或结算${tradingStatus.pauseReason ? ` · ${tradingStatus.pauseReason}` : ''}`
                  : '当前休市中，无法买入或结算'}
              </p>
            </div>
          )}

          {/* 账户汇总 */}
          <div className="mx-3 mt-3 bg-white rounded-xl shadow-sm p-4 animate-fade-up">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-gray-400 text-xs mb-1">账户总资产</p>
                <p className={`text-brown-900 text-sm font-bold rounded px-1 py-0.5 transition-colors duration-300 ${flashClass(flashTotal)}`}>¥{totalAssets.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-xs mb-1">可用资金</p>
                <p className={`text-green-600 text-sm font-bold rounded px-1 py-0.5 transition-colors duration-300 ${flashClass(flashAvail)}`}>¥{liveAvailable.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</p>
              </div>
              <div className="text-center">
                <p className="text-gray-400 text-xs mb-1">已冻结保证金</p>
                <p className="text-amber-500 text-sm font-bold">¥{accountSummary.usedMargin.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={`text-sm font-semibold rounded px-1 py-0.5 transition-colors duration-300 ${totalFloatingProfit >= 0 ? 'text-red-500' : 'text-emerald-500'} ${flashClass(flashProfit)}`}>
                  订单盈亏 {totalFloatingProfit >= 0 ? '+' : ''}{totalFloatingProfit.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="flex items-center gap-3 text-xs">
                <span className="text-gray-500">买入均价 <span className={`font-medium ${avgBuyPrice > 0 ? 'text-brown-900' : 'text-gray-400'}`}>{avgBuyPrice > 0 ? `¥${avgBuyPrice.toFixed(2)}` : '—'}</span></span>
              </div>
            </div>
          </div>

          {/* 筛选 */}
          <div className="mx-3 mt-3 flex items-center justify-between">
            <div className="relative" data-dropdown="type">
              <button
                onClick={() => setTypeOpen(!typeOpen)}
                className="flex items-center gap-1 bg-white rounded-lg px-3 py-1.5 text-xs text-gray-600 shadow-sm cursor-pointer whitespace-nowrap"
              >
                {typeFilter === 'all' ? '全部类型' : typeFilter === 'IN' ? '买入' : '卖出'}
                <i className={`ri-arrow-down-s-line text-gray-400 transition-transform ${typeOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {typeOpen && (
                <div className="absolute top-full left-0 mt-1.5 z-50 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden min-w-[100px]">
                  {(['all', 'IN', 'OUT'] as const).map((k) => (
                    <button key={k} onClick={() => { setTypeFilter(k); setTypeOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-xs cursor-pointer whitespace-nowrap transition-colors ${typeFilter === k ? 'text-amber-600 bg-amber-50 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>
                      {k === 'all' ? '全部类型' : k === 'IN' ? '买入' : '卖出'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 持仓列表 */}
          <div className="mx-3 mt-3">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
              </div>
            ) : error ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <p className="text-brown-500 text-sm mb-3">订单数据加载失败</p>
                <button onClick={fetchData} className="px-4 py-2 rounded-full bg-gold-500 text-brown-950 text-xs font-medium cursor-pointer whitespace-nowrap">重新加载</button>
              </div>
            ) : filteredPositions.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <p className="text-brown-400 text-sm">暂无订单</p>
              </div>
            ) : (
              <>
                {filteredPositions.map((pos) => (
                  <PositionCard
                    key={pos.id}
                    pos={pos}
                    onPerform={() => navigate('/payment')}
                    onAddMargin={() => openModal('addMargin', pos.id)}
                    onClose={() => openModal('close', pos.id)}
                  />
                ))}
              </>
            )}
          </div>
        </>
      )}

      {activeTab === 'close' && (
        <div className="mx-3 mt-3">
          {closedPositions.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                <i className="ri-file-list-3-line text-amber-200 text-2xl"></i>
              </div>
              <p className="text-gray-400 text-sm">暂无已完成记录</p>
            </div>
          ) : (
            closedPositions.map((pos, idx) => (
              <div key={pos.id} className="bg-white rounded-xl shadow-sm overflow-hidden mb-3 p-4 animate-fade-up" style={{ animationDelay: `${0.1 + idx * 0.1}s` }}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="bg-amber-100 text-amber-600 text-[10px] px-1.5 py-0.5 rounded font-medium">{pos.direction === 'IN' ? '买入 IN' : '卖出 OUT'}</span>
                    <span className="text-brown-900 text-sm font-semibold">{pos.name} {pos.weight}g</span>
                  </div>
                  <span className="text-gray-400 text-xs">{pos.closeTime}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-center flex-1">
                    <p className="text-brown-900 text-lg font-bold">{pos.openPrice.toFixed(2)}</p>
                    <p className="text-gray-400 text-xs mt-0.5">开仓价</p>
                  </div>
                  <div className="text-center flex-1">
                    <p className="text-brown-900 text-lg font-bold">{pos.closePrice.toFixed(2)}</p>
                    <p className="text-gray-400 text-xs mt-0.5">平仓价</p>
                  </div>
                  <div className="text-center flex-1">
                    <p className={`text-lg font-bold ${pos.profit >= 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                      {pos.profit >= 0 ? '+' : ''}{pos.profit.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                    </p>
                    <p className="text-gray-400 text-xs mt-0.5">盈亏</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div className="h-6" />

      {/* ===== 追加保证金弹窗 ===== */}
      <BottomSheet open={modalType === 'addMargin'} onClose={closeModal} title="追加保证金">
        {activeModalPos && (
          <div className="px-4 pb-6">
            <div className="bg-amber-50 rounded-xl p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-brown-600 text-sm font-medium">{activeModalPos.name} {activeModalPos.weight}g</span>
                <div className={`text-xs px-2 py-0.5 rounded-full font-medium ${getRiskInfo(activeModalPos.marginRate, activeModalPos.liquidationLine).level === 'normal' ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                  保证金率 {activeModalPos.marginRate.toFixed(1)}%
                </div>
              </div>
              <MarginRateBar marginRate={activeModalPos.marginRate} liquidationLine={activeModalPos.liquidationLine} />
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <p className="text-gray-500 text-xs">当前保证金</p>
                  <p className="text-brown-900 text-sm font-bold">¥{activeModalPos.currentMargin.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">当前可用资金</p>
                  <p className="text-green-600 text-sm font-bold">¥{liveAvailable.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</p>
                </div>
              </div>
            </div>

            <label className="text-brown-700 text-sm font-medium block mb-2">请输入追加金额</label>
            <div className="relative mb-3">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brown-500 text-sm">¥</span>
              <input
                type="number"
                inputMode="decimal"
                value={inputAmount}
                onChange={(e) => { setInputAmount(e.target.value); setModalError(''); }}
                placeholder="请输入追加金额"
                className="w-full pl-8 pr-4 py-3 rounded-xl border border-gray-200 text-brown-900 text-base focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500"
              />
            </div>

            <div className="flex gap-2 mb-4">
              {[
                { label: '5000', value: 5000 },
                { label: '10000', value: 10000 },
                { label: '20000', value: 20000 },
              ].map(({ label, value }) => (
                <button
                  key={label}
                  onClick={() => { setInputAmount(String(value)); setModalError(''); }}
                  className="flex-1 py-2 rounded-lg text-xs font-medium cursor-pointer active:scale-95 transition-transform bg-brown-50 text-brown-700"
                >
                  ¥{parseInt(label).toLocaleString()}
                </button>
              ))}
              <button
                onClick={() => { setInputAmount(String(Math.floor(liveAvailable * 100) / 100)); setModalError(''); }}
                className="flex-1 py-2 rounded-lg bg-gold-100 text-gold-700 text-xs font-medium cursor-pointer active:scale-95 transition-transform"
              >
                全部
              </button>
            </div>

            {(() => {
              const rawAmount = parseFloat(inputAmount) || 0;
              if (rawAmount <= 0) return null;
              const newMargin = activeModalPos.currentMargin + rawAmount;
              // 直接复用卡片同一套保证金率算法，保证预估与实际口径完全一致
              const { marginRate: newRate } = calcPositionLive(
                activeModalPos.direction,
                activeModalPos.weight,
                activeModalPos.openPrice,
                activeModalPos.settlePrice || activeModalPos.currentPrice,
                newMargin,
                activeModalPos.originalMargin,
              );
              const liqPct = activeModalPos.liquidationLine * 100;
              const escaped = newRate > liqPct + 10;
              return (
                <div className="bg-green-50 rounded-lg p-3 mb-3 text-xs text-green-700">
                  追加后保证金将变为 ¥{newMargin.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}，预计保证金率: {newRate.toFixed(1)}%
                  {escaped && <span className="block mt-1 text-green-600">✓ 已脱离预警区间，风险降低</span>}
                </div>
              );
            })()}

            {modalError && (
              <p className="text-red-500 text-xs mb-3 flex items-center gap-1">
                <i className="ri-error-warning-line"></i>{modalError}
              </p>
            )}

            <button
              onClick={handleAddMarginConfirm}
              disabled={submitting || !inputAmount || parseFloat(inputAmount) <= 0}
              className={`w-full py-3 rounded-xl text-white text-sm font-semibold cursor-pointer transition-all flex items-center justify-center gap-2 ${submitting || !inputAmount || parseFloat(inputAmount) <= 0 ? 'bg-gray-300 cursor-not-allowed' : 'bg-amber-500 active:scale-[0.98]'}`}
            >
              {submitting && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>}
              {submitting ? '处理中' : '立即追加'}
            </button>
          </div>
        )}
      </BottomSheet>

      {/* ===== 解除预约弹窗 ===== */}
      <BottomSheet open={modalType === 'close'} onClose={closeModal} title="确认解除预约">
        {activeModalPos && (
          <div className="px-4 pb-6">
            <div className="bg-brown-50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <span className="bg-amber-100 text-amber-600 text-[10px] px-1.5 py-0.5 rounded font-medium">{activeModalPos.direction === 'IN' ? '买入 IN' : '卖出 OUT'}</span>
                <span className="text-brown-900 text-sm font-bold">{activeModalPos.name} {activeModalPos.weight}g</span>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-2">
                <div>
                  <p className="text-gray-400 text-xs">开仓价</p>
                  <p className="text-brown-900 text-sm font-bold">{activeModalPos.openPrice.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-xs">当前价</p>
                  <p className="text-brown-900 text-sm font-bold">{activeModalPos.currentPrice.toFixed(2)}</p>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-2 flex items-center justify-between">
                <span className="text-brown-700 text-sm font-medium">预计盈亏</span>
                <span className={`text-lg font-bold ${activeModalPos.liveProfit >= 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                  {activeModalPos.liveProfit >= 0 ? '+' : ''}{activeModalPos.liveProfit.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3 mb-4 flex items-start gap-2">
              <i className="ri-information-line text-amber-500 mt-0.5"></i>
              <p className="text-amber-700 text-xs leading-relaxed">
                解除预约后，当前保证金 ¥{activeModalPos.currentMargin.toLocaleString('zh-CN', { minimumFractionDigits: 2 })} 将释放回账户可用资金。
              </p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 mb-4 flex items-start gap-2">
              <i className="ri-alarm-warning-line text-red-500 mt-0.5"></i>
              <p className="text-red-600 text-xs leading-relaxed">解除预约不可撤销，请确认后操作。</p>
            </div>
            <div className="flex gap-3">
              <button onClick={closeModal} className="flex-1 py-3 rounded-xl bg-gray-100 text-brown-700 text-sm font-medium cursor-pointer active:scale-95 transition-transform">取消</button>
              <button onClick={handleCloseConfirm} disabled={closing} className="flex-1 py-3 rounded-xl bg-red-500 text-white text-sm font-semibold cursor-pointer active:scale-95 transition-transform disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2">
                {closing && <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"></span>}
                {closing ? '处理中' : '确认解除'}
              </button>
            </div>
          </div>
        )}
      </BottomSheet>

      {/* ===== 风险预警弹窗 ===== */}
      {riskWarning && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-6">
          <div className="absolute inset-0 bg-black/50" onClick={() => setRiskWarning(null)}></div>
          <div className="relative bg-white rounded-2xl p-6 w-full max-w-sm animate-fade-up">
            <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${riskWarning.level === 'high' ? 'bg-red-100' : 'bg-amber-100'}`}>
              <i className={`ri-alarm-warning-line text-2xl ${riskWarning.level === 'high' ? 'text-red-500' : 'text-amber-500'}`}></i>
            </div>
            <h3 className={`text-center text-base font-bold mb-2 ${riskWarning.level === 'high' ? 'text-red-600' : 'text-amber-600'}`}>
              {riskWarning.level === 'high' ? '⚠️ 高风险警告' : '⚡ 风险提醒'}
            </h3>
            <p className="text-gray-600 text-sm text-center mb-5 leading-relaxed">
              {riskWarning.level === 'high'
                ? '您的订单保证金率已低于25%，接近强平线，请尽快追加保证金，否则系统将自动解除预约。'
                : '您的订单保证金率低于30%，建议追加保证金以降低强平风险。'}
            </p>
            <div className="flex gap-3">
              <button onClick={() => setRiskWarning(null)} className="flex-1 py-2.5 rounded-xl bg-gray-100 text-brown-700 text-sm font-medium cursor-pointer">稍后处理</button>
              <button onClick={() => { setRiskWarning(null); openModal('addMargin', riskWarning.posId); }}
                className={`flex-1 py-2.5 rounded-xl text-white text-sm font-semibold cursor-pointer ${riskWarning.level === 'high' ? 'bg-red-500' : 'bg-amber-500'}`}>
                立即追加
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}